import { Injectable, NgZone } from '@angular/core';
import { Subject, Subscription, fromEvent, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import * as echarts from 'echarts';

@Injectable({ providedIn: 'root' })
export class ResponsiveLayoutService {
  private reflowSubject = new Subject<void>();
  reflow$ = this.reflowSubject.asObservable();
  private resizeSub?: Subscription;
  private initialized = false;
  private rowCount = 0;
  private _screenFloating = true;
  private compactLayoutFitOverride: boolean | null = null;

  // Dashboard-level auto-resize + responsive grid for ECharts instances
  private chartResizeObserver?: ResizeObserver;
  private chartMutationObserver?: MutationObserver;
  private observedElements = new WeakSet<Element>();
  private gridAppliedElements = new WeakSet<Element>();
  private gridScanTimer?: ReturnType<typeof setInterval>;
  private autoResizeRoot?: HTMLElement;
  private fullscreenScaledElement?: HTMLElement;

  // Track font scale applied to each chart element to avoid compounding
  private fontScaleMap = new Map<Element, number>();
  // Track a representative post-scaling font size per element to detect option resets
  private fontFingerprints = new Map<Element, number>();
  private lastEffectiveScale = 1;
  private readonly dprZoomEnabled = false;
  private defaultWidgetHeight = '20vh';
  private readonly focusModeBaselineArea = 1280 * 720;

  get screenFloating(): boolean {
    return this._screenFloating;
  }

  getDefaultWidgetHeight(): string {
    return this.defaultWidgetHeight;
  }

  setDefaultWidgetHeight(height: string): void {
    const normalizedHeight = (height || '').trim();
    if (!normalizedHeight) return;
    this.defaultWidgetHeight = normalizedHeight;
  }

  resolveHeightToPixels(heightOrWidget: unknown): number | null {
    const resolvedHeight = this.resolveWidgetHeight(heightOrWidget);
    return this.measureCssHeightInPixels(resolvedHeight);
  }

  resolveWidgetHeight(heightOrWidget: unknown): string {
    const candidate = this.resolveHeightCandidate(heightOrWidget);
    const normalized = this.normalizeHeightValue(candidate);
    return normalized ?? this.defaultWidgetHeight;
  }

  private resolveHeightCandidate(heightOrWidget: unknown): unknown {
    if (
      heightOrWidget &&
      typeof heightOrWidget === 'object' &&
      !Array.isArray(heightOrWidget)
    ) {
      const widget = heightOrWidget as Record<string, unknown>;
      const chartConfig =
        widget['chartConfig'] && typeof widget['chartConfig'] === 'object'
          ? widget['chartConfig'] as Record<string, unknown>
          : null;
      const width = window.innerWidth;

      if (width < 768 && widget['heightSm'] != null && widget['heightSm'] !== '') {
        return widget['heightSm'];
      }

      if (width < 1024 && widget['heightMd'] != null && widget['heightMd'] !== '') {
        return widget['heightMd'];
      }

      if (widget['heightLg'] != null && widget['heightLg'] !== '') {
        return widget['heightLg'];
      }

      if (widget['height'] != null && widget['height'] !== '') {
        return widget['height'];
      }

      if (chartConfig?.['height'] != null && chartConfig['height'] !== '') {
        return chartConfig['height'];
      }

      return null;
    }

    return heightOrWidget;
  }

  private normalizeHeightValue(height: unknown): string | null {
    if (height === undefined || height === null || height === '') {
      return null;
    }

    if (typeof height === 'number') {
      return `${height}px`;
    }

    if (typeof height === 'string') {
      const trimmed = height.trim();
      if (!trimmed) return null;
      if (/^\d+(\.\d+)?$/.test(trimmed)) {
        return `${trimmed}px`;
      }
      return trimmed;
    }

    return null;
  }

  private measureCssHeightInPixels(height: string): number | null {
    const normalized = (height || '').trim();
    if (!normalized || typeof document === 'undefined') return null;

    if (/^\d+(\.\d+)?px$/i.test(normalized)) {
      return Math.round(parseFloat(normalized));
    }

    if (/^\d+(\.\d+)?$/i.test(normalized)) {
      return Math.round(parseFloat(normalized));
    }

    const probe = document.createElement('div');
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.left = '-9999px';
    probe.style.top = '0';
    probe.style.width = '0';
    probe.style.minHeight = '0';
    probe.style.maxHeight = 'none';
    probe.style.padding = '0';
    probe.style.border = '0';
    probe.style.margin = '0';
    probe.style.height = normalized;

    document.body.appendChild(probe);
    const measured = parseFloat(getComputedStyle(probe).height || '');
    document.body.removeChild(probe);

    return Number.isFinite(measured) && measured > 0 ? Math.round(measured) : null;
  }

  /**
   * Effective screen floating state.
   * - screenFloating: true  => normal scroll mode
   * - screenFloating: false => desktop compact mode only while it still fits
   * Small/medium screens always stay in scroll mode.
   */
  get effectiveScreenFloating(): boolean {
    return !this.shouldUseCompactNoScroll();
  }

  constructor(private ngZone: NgZone) {}

  init(): void {
    this.ensureDprZoom();

    if (this.initialized) return;
    this.initialized = true;

    this.updateCSSVariables();

    this.ngZone.runOutsideAngular(() => {
      const resizeStreams = [fromEvent(window, 'resize')];
      if (window.visualViewport) {
        resizeStreams.push(fromEvent(window.visualViewport, 'resize'));
      }

      this.resizeSub = merge(...resizeStreams)
        .pipe(debounceTime(150))
        .subscribe(() => {
          this.updateCSSVariables();
          this.ngZone.run(() => this.reflowSubject.next());
        });
    });
  }

  setRowCount(count: number): void {
    this.rowCount = count;
    this.updateCSSVariables();
    this.reflowSubject.next();
  }

  setCompactLayoutFitOverride(fits: boolean | null): void {
    if (this.compactLayoutFitOverride === fits) return;
    this.compactLayoutFitOverride = fits;
    this.updateCSSVariables();
    this.reflowSubject.next();
  }

  setScreenFloating(value: boolean): void {
    this._screenFloating = value;
    if (value) {
      this.compactLayoutFitOverride = null;
    }
    // updateCSSVariables handles adaptive compact mode based on screen size,
    // viewport height, row count, and zoom.
    this.updateCSSVariables();
    // Reset scroll position when compact fit mode becomes active.
    if (this.shouldUseCompactNoScroll() && document.documentElement.classList.contains('dvm-no-scroll')) {
      const sidenavContent = document.querySelector('mat-sidenav-content, .mat-sidenav-content');
      if (sidenavContent) {
        sidenavContent.scrollTop = 0;
      }
    }
    this.reflowSubject.next();
  }

  triggerReflow(): void {
    this.updateCSSVariables();
    this.reflowSubject.next();
  }

  /**
   * Re-apply the DPR zoom bypass if it should be active but isn't.
   * Handles HMR / hot-reload where index.html inline script doesn't re-execute,
   * so developers don't need to restart ng serve after code changes.
   */
  /**
   * Target DPR for the zoom bypass.
   * Ultra-high-res screens (physical ≥ 3000px, DPR ≥ 2) bypass to 150%.
   * Normal screens bypass to 100%.
   */
  private getTargetDpr(): number {
    const dpr = window.devicePixelRatio || 1;
    const physicalWidth = Math.round((window.screen.width || 0) * dpr);
    if (physicalWidth >= 3000 && dpr >= 2) return 1.5;
    return 1;
  }

  private ensureDprZoom(): void {
    if (!this.dprZoomEnabled) {
      const tag = document.getElementById('dvm-dpr-zoom');
      if (tag) tag.remove();
      document.documentElement.style.setProperty('--dvm-dpr-active', '0');
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    const isLargeScreen = window.screen.width >= 1024;
    const shouldBeActive = dpr !== 1 && isLargeScreen;
    const currentlyActive = this.isDprZoomActive();

    if (shouldBeActive && !currentlyActive) {
      const targetDpr = this.getTargetDpr();
      const z = targetDpr / dpr;
      const s = (dpr / targetDpr) * 100;
      let tag = document.getElementById('dvm-dpr-zoom') as HTMLStyleElement;
      if (!tag) {
        tag = document.createElement('style');
        tag.id = 'dvm-dpr-zoom';
        document.head.appendChild(tag);
      }
      tag.textContent =
        'html{height:100%;overflow:hidden}' +
        'body{transform:scale(' + z + ');transform-origin:0 0;width:' + s + 'vw;height:' + s + 'vh;overflow:hidden}' +
        ':root{--dvh:' + s + 'vh;--dvm-dpr-active:1;--dvm-target-dpr:' + targetDpr + '}';
    } else if (!shouldBeActive && currentlyActive) {
      const tag = document.getElementById('dvm-dpr-zoom');
      if (tag) tag.remove();
      document.documentElement.style.setProperty('--dvm-dpr-active', '0');
    }
  }

  /**
   * Detect if DPR zoom bypass is active (desktop with OS scaling).
   * index.html sets --dvm-dpr-active:1 when zoom is applied.
   */
  private isDprZoomActive(): boolean {
    if (!this.dprZoomEnabled) return false;
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--dvm-dpr-active')?.trim() === '1';
  }

  /**
   * Get the effective viewport height in CSS pixels.
   * When DPR zoom is active, body is dpr*100vh, so multiply innerHeight by dpr.
   * When not active (mobile/tablet or dpr=1), use innerHeight directly.
   */
  private getEffectiveVh(): number {
    if (this.isDprZoomActive()) {
      const dpr = window.devicePixelRatio || 1;
      const targetDpr = this.getTargetDpr();
      // body zoom = targetDpr/dpr, so effective height = innerHeight * dpr/targetDpr
      return Math.round(window.innerHeight * dpr / targetDpr);
    }
    return window.innerHeight;
  }

  /**
   * Screen size category for adaptive overheads and minimums.
   */
  private getScreenCategory(): 'small' | 'medium' | 'large' {
    const width = window.innerWidth;
    if (width < 768) return 'small';       // Mobile
    if (width < 1024) return 'medium';     // Tablet
    return 'large';                         // Laptop / Monitor
  }

  /**
   * Chart-only zoom factor (does not transform body/layout).
   * Keeps charts readable on Windows display scaling (125/150/175%) and browser zoom.
   */
  private getChartZoomScale(): number {
    const vvScale = window.visualViewport?.scale;
    if (typeof vvScale === 'number' && isFinite(vvScale) && vvScale > 1) {
      return Math.min(2, vvScale);
    }

    const dpr = window.devicePixelRatio || 1;
    const isWindows = /Windows/i.test(navigator.userAgent || '');
    if (isWindows && dpr > 1 && dpr <= 2) {
      return dpr;
    }

    return 1;
  }

  /**
   * screenFloating:false is adaptive on desktop/tablet:
   * keep the dashboard in compact one-screen mode only while each logical row
   * can still retain a minimum readable chart height. Once zoom or viewport
   * reduction pushes below that threshold, fall back to normal page scroll.
   */
  private shouldUseCompactNoScroll(): boolean {
    const screen = this.getScreenCategory();
    if (screen === 'small') return false;
    if (this._screenFloating) return false;
    if (this.compactLayoutFitOverride !== null) return this.compactLayoutFitOverride;
    return false;
  }

  getCompactMinimumChartHeight(): number {
    return Math.round(150 * this.getChartZoomScale());
  }

  getEstimatedCompactRequiredHeight(rowGap = 16): number {
    const screen = this.getScreenCategory();
    if (screen === 'small') return Number.POSITIVE_INFINITY;
    if (this._screenFloating) return 0;
    if (this.rowCount <= 0) return 0;

    return (
      (this.rowCount * this.getCompactMinimumChartHeight()) +
      (Math.max(0, this.rowCount - 1) * rowGap)
    );
  }

  estimateCompactLayoutFit(availableHeight: number): boolean {
    const screen = this.getScreenCategory();
    if (screen === 'small') return false;
    if (this._screenFloating) return false;
    if (this.rowCount <= 0) return availableHeight > 0;

    return availableHeight >= this.getEstimatedCompactRequiredHeight();
  }

  /**
   * Compute a font scale factor for large/TV screens.
   * On screens wider than 1920 CSS-px the factor grows linearly so
   * text stays readable at 4K / TV resolutions.
   * Returns 1 for normal screens (≤ 1920px).
   */
  getFontScale(): number {
    // Determine the effective viewport width:
    // - DPR zoom active: body zoom = targetDpr/dpr, effective width = innerWidth * dpr/targetDpr
    // - No DPR zoom: use the larger of screen.width and innerWidth
    //   (screen.width works on real TVs, innerWidth works in DevTools)
    const dpr = window.devicePixelRatio || 1;
    const targetDpr = this.getTargetDpr();
    const w = this.isDprZoomActive()
      ? Math.round((window.innerWidth || 0) * dpr / targetDpr)
      : Math.max(window.screen.width || 0, window.innerWidth || 0);

    // Tablet: shrink ECharts fonts slightly to prevent label overlap
    if (w >= 768 && w < 1024) return 0.85;

    if (w <= 1920) return 1;
    // Linear scale: 1 at 1920, 2 at 3840, capped at 3
    return Math.min(3, w / 1920);
  }

  private updateCSSVariables(): void {
    const vh = this.getEffectiveVh();
    const root = document.documentElement;
    const screen = this.getScreenCategory();
    const chartZoomScale = this.getChartZoomScale();
    const wasNoScroll = root.classList.contains('dvm-no-scroll');

    // Font scale for large / TV screens.
    // When DPR zoom is active, body has zoom:targetDpr/dpr which shrinks everything,
    // so multiply fontScale by dpr/targetDpr to compensate for that shrinkage.
    // CSS font-scale: only used for scaling UP (large/TV). Tablet shrink (< 1) is
    // applied only to ECharts labels, not to CSS-based card headers / sidenav.
    const rawFontScale = this.getFontScale();
    const cssFontScale = Math.max(1, rawFontScale); // CSS variable never goes below 1
    const dprZoom = this.isDprZoomActive();
    const dpr = window.devicePixelRatio || 1;
    const targetDpr = this.getTargetDpr();
    const dprCompensation = dpr / targetDpr; // e.g., 2.5/1.5=1.67 for ultra-high-res
    const fontScale = (dprZoom && dprCompensation !== 1 && cssFontScale > 1)
      ? cssFontScale * dprCompensation
      : (dprZoom && dprCompensation !== 1) ? dprCompensation : cssFontScale;
    root.style.setProperty('--dvm-root-font-scale', fontScale.toString());
    root.style.setProperty('--dvm-font-scale', fontScale.toString());
    this.applyFocusModeFontScale(fontScale);

    // Re-scale ECharts fonts if scale changed (e.g., window moved between monitors)
    this.rescaleAllChartFonts();

    // Screen-size-aware overheads (smaller screens have less chrome)
    const overheads = {
      small:  { fixed: 120, row: 30, minChart: 180, minChartNoScroll: 120 },
      medium: { fixed: 150, row: 45, minChart: 200, minChartNoScroll: 100 },
      large:  { fixed: 180, row: 60, minChart: 200, minChartNoScroll: 80 },
    }[screen];

    // Desktop compact mode stays active only while the dashboard still has
    // enough height to keep charts readable. Otherwise revert to scroll mode.
    const effectiveNoScroll = this.shouldUseCompactNoScroll();

    // Update class on root for CSS to know the mode
    if (effectiveNoScroll) {
      root.classList.add('dvm-no-scroll');
    } else {
      root.classList.remove('dvm-no-scroll');
    }

    if (effectiveNoScroll && !wasNoScroll) {
      const sidenavContent = document.querySelector('mat-sidenav-content, .mat-sidenav-content');
      if (sidenavContent) {
        sidenavContent.scrollTop = 0;
      }
    }

    if (effectiveNoScroll && this.rowCount > 0) {
      // No-scroll: fit everything in viewport — divide by rowCount
      const noScrollOverheads = {
        small:  { fixed: 120, row: 30 },
        medium: { fixed: 140, row: 40 },
        large:  { fixed: 190, row: 54 },
      }[screen];

      const available = vh - noScrollOverheads.fixed - (this.rowCount * noScrollOverheads.row);
      const baseNoScroll = Math.max(overheads.minChartNoScroll, Math.round(available / this.rowCount));
      const chartHeight = Math.round(baseNoScroll * chartZoomScale);
      root.style.setProperty('--dvm-chart-height', `${chartHeight}px`);
      root.style.setProperty('--dvm-chart-height-md', `${Math.round(chartHeight * 1.1)}px`);
      root.style.setProperty('--dvm-chart-height-lg', `${Math.round(chartHeight * 1.25)}px`);
      root.style.setProperty('--dvm-chart-height-xl', `${Math.round(chartHeight * 1.5)}px`);
    } else {
      // Scroll mode: each chart gets a comfortable % of viewport height.
      // NOT divided by rowCount — users can scroll, so charts don't need to
      // squeeze into one screen.
      const factor = screen === 'small' ? 0.45 : screen === 'medium' ? 0.40 : 0.35;
      const baseChartHeight = Math.max(overheads.minChart, Math.round((vh - overheads.fixed) * factor));
      const chartHeight = Math.round(baseChartHeight * chartZoomScale);
      root.style.setProperty('--dvm-chart-height', `${chartHeight}px`);

      const baseChartHeightMd = Math.max(overheads.minChart + 50, Math.round((vh - overheads.fixed) * (factor + 0.07)));
      const chartHeightMd = Math.round(baseChartHeightMd * chartZoomScale);
      root.style.setProperty('--dvm-chart-height-md', `${chartHeightMd}px`);

      const baseChartHeightLg = Math.max(overheads.minChart + 100, Math.round((vh - overheads.fixed) * (factor + 0.15)));
      const chartHeightLg = Math.round(baseChartHeightLg * chartZoomScale);
      root.style.setProperty('--dvm-chart-height-lg', `${chartHeightLg}px`);

      // XL: full-panel widgets (entity table/form, gantt) — ~70% of viewport.
      // At 1920x1080: ~630px. At 1366x768: ~450px. At 4K: ~1500px.
      const baseChartHeightXl = Math.max(overheads.minChart + 250, Math.round((vh - overheads.fixed) * (factor + 0.35)));
      const chartHeightXl = Math.round(baseChartHeightXl * chartZoomScale);
      root.style.setProperty('--dvm-chart-height-xl', `${chartHeightXl}px`);
    }
  }

  /**
   * Dashboard-level auto-resize AND responsive grid for ALL ECharts instances.
   *
   * 1. ResizeObserver: auto-resizes charts when container dimensions change
   *    (initial flex layout, window resize, sidebar toggle).
   * 2. Grid scan: periodically detects new ECharts instances and applies
   *    responsive percentage-based grid config (containLabel: true) to
   *    cartesian charts. This prevents fixed-pixel grid padding from
   *    consuming chart area in compact containers.
   *
   * No per-component code needed — this handles everything from the dashboard.
   */
  startChartAutoResize(containerEl?: HTMLElement): void {
    const root = containerEl || document.body;
    this.autoResizeRoot = root;

    this.chartResizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const chart = echarts.getInstanceByDom(entry.target as HTMLElement);
        chart?.resize();
      }
    });

    // Observe any existing chart containers
    this.observeChartContainers(root);

    // Watch for dynamically added chart containers (lazy-loaded components)
    this.chartMutationObserver = new MutationObserver(() => {
      this.observeChartContainers(root);
      this.applyResponsiveGridToAll(root);
    });
    this.chartMutationObserver.observe(root, { childList: true, subtree: true });

    // Periodically scan for new ECharts instances that need responsive grid.
    // Components call echarts.init() + setOption() asynchronously (after API calls),
    // so we poll until all charts are processed, then stop.
    this.gridScanTimer = setInterval(() => {
      this.applyResponsiveGridToAll(root);
    }, 500);
  }

  private observeChartContainers(root: HTMLElement): void {
    root.querySelectorAll('div[id]').forEach((el) => {
      if (!this.observedElements.has(el)) {
        this.observedElements.add(el);
        this.chartResizeObserver?.observe(el);
      }
    });
  }

  /**
   * Find all ECharts instances in the dashboard and apply:
   * 1. Font scaling — DPR compensation or large/TV screen scaling
   *    (re-applied when a chart rebuilds its option via setOption)
   * 2. Responsive grid defaults for cartesian charts (one-time)
   */
  private applyResponsiveGridToAll(root: HTMLElement): void {
    root.querySelectorAll('div[id]').forEach((el) => {
      const chart = echarts.getInstanceByDom(el as HTMLElement);
      if (!chart) return;

      const option = chart.getOption() as any;
      if (!option) return;

      const effectiveScale = this.getEffectiveChartScaleForElement(el);
      const previousScale = this.fontScaleMap.get(el) ?? 1;
      const storedFingerprint = this.fontFingerprints.get(el);
      const currentFingerprint = this.getFontFingerprint(option);
      const scaleChanged = Math.abs(effectiveScale - previousScale) >= 0.01;
      const fingerprintReset = storedFingerprint != null
        && currentFingerprint > 0
        && Math.abs(currentFingerprint - storedFingerprint) > Math.max(2, storedFingerprint * 0.25);

      if (scaleChanged) {
        const correction = effectiveScale / previousScale;
        if (Math.abs(correction - 1) >= 0.01) {
          this.scaleChartVisuals(chart, option, correction);
        }
        const scaledOption = chart.getOption() as any;
        this.fontScaleMap.set(el, effectiveScale);
        this.fontFingerprints.set(el, this.getFontFingerprint(scaledOption));
      } else if (effectiveScale !== 1 && (storedFingerprint == null || fingerprintReset)) {
        this.scaleChartVisuals(chart, option, effectiveScale);
        const scaledOption = chart.getOption() as any;
        this.fontScaleMap.set(el, effectiveScale);
        this.fontFingerprints.set(el, this.getFontFingerprint(scaledOption));
      } else if (storedFingerprint == null) {
        this.fontScaleMap.set(el, effectiveScale);
        this.fontFingerprints.set(el, currentFingerprint);
      }

      // --- Grid (one-time per element) ---
      if (this.gridAppliedElements.has(el)) return;

      // Only apply grid to cartesian charts (have xAxis or yAxis)
      const hasCartesianAxis = option.xAxis?.length > 0 || option.yAxis?.length > 0;
      if (!hasCartesianAxis) {
        this.gridAppliedElements.add(el);
        return;
      }

      // Apply responsive percentage-based grid with containLabel,
      // and add hideOverlap to all axes to prevent label collisions on compact screens.
      // Preserve any existing grid values that are already pixel numbers, pixel strings,
      // or sufficient percentages — only enforce minimums when the value is missing/too small.
      const existingGrid = option.grid?.[0] || {};
      const elHeight = (el as HTMLElement).clientHeight || 300;
      const elWidth = (el as HTMLElement).clientWidth || 600;
      const pctToPx = (pct: number, axis: 'h' | 'v') => Math.round((pct / 100) * (axis === 'h' ? elWidth : elHeight));
      const asPx = (v: any, axis: 'h' | 'v'): number | null => {
        if (v == null) return null;
        if (typeof v === 'number') return v;
        if (typeof v === 'string') {
          const t = v.trim();
          if (t.endsWith('%')) { const p = parseFloat(t); return Number.isFinite(p) ? pctToPx(p, axis) : null; }
          if (t.endsWith('px')) { const p = parseFloat(t); return Number.isFinite(p) ? p : null; }
          const p = parseFloat(t); return Number.isFinite(p) ? p : null;
        }
        return null;
      };
      const enforceMin = (existing: any, minPct: number, axis: 'h' | 'v') => {
        const existingPx = asPx(existing, axis);
        const minPx = pctToPx(minPct, axis);
        if (existingPx == null || existingPx < minPx) return minPct + '%';
        // Keep original value (px number, px string, or % string) untouched
        return existing;
      };
      const axisPatch: any = {
        grid: {
          top: enforceMin(existingGrid.top, 10, 'v'),
          left: enforceMin(existingGrid.left, 2, 'h'),
          right: enforceMin(existingGrid.right, 4, 'h'),
          bottom: enforceMin(existingGrid.bottom, 5, 'v'),
          containLabel: true,
        },
      };

      // hideOverlap: ECharts will automatically hide overlapping axis labels
      if (option.xAxis?.length) {
        axisPatch.xAxis = option.xAxis.map(() => ({
          axisLabel: { hideOverlap: true },
        }));
      }
      if (option.yAxis?.length) {
        axisPatch.yAxis = option.yAxis.map(() => ({
          axisLabel: { hideOverlap: true },
        }));
      }

      chart.setOption(axisPatch);

      this.gridAppliedElements.add(el);
    });
  }

  private getEffectiveChartScaleForElement(el?: Element | null): number {
    return this.getBaseChartScale() * this.getFocusModeScaleForElement(el);
  }

  private getBaseChartScale(): number {
    const dprZoomActive = this.isDprZoomActive();
    const dpr = window.devicePixelRatio || 1;
    const targetDpr = this.getTargetDpr();
    const rawFontScale = this.getFontScale();
    const dprFactor = (dprZoomActive && dpr !== 1) ? dpr / targetDpr : 1;

    if (rawFontScale < 1) {
      return rawFontScale;
    }

    if (dprFactor > 1 && rawFontScale > 1) {
      return dprFactor * rawFontScale;
    }

    return Math.max(dprFactor, rawFontScale);
  }

  private getFocusModeScaleForElement(el?: Element | null): number {
    const fullscreenEl = document.fullscreenElement as HTMLElement | null;
    if (!fullscreenEl || !el || !fullscreenEl.contains(el)) {
      return 1;
    }

    const rect = fullscreenEl.getBoundingClientRect();
    const area = Math.max(1, rect.width * rect.height);
    const scale = Math.sqrt(area / this.focusModeBaselineArea);
    return this.clampScale(scale, 1.15, 1.9);
  }

  private applyFocusModeFontScale(rootFontScale: number): void {
    const fullscreenEl = document.fullscreenElement as HTMLElement | null;

    if (this.fullscreenScaledElement && this.fullscreenScaledElement !== fullscreenEl) {
      this.clearFocusModeFontScale(this.fullscreenScaledElement);
      this.fullscreenScaledElement = undefined;
    }

    if (!fullscreenEl) {
      return;
    }

    const focusScale = this.getFocusModeScaleForElement(fullscreenEl);
    fullscreenEl.style.setProperty('--dvm-focus-scale', focusScale.toFixed(3));
    fullscreenEl.style.setProperty('--dvm-font-scale', (rootFontScale * focusScale).toFixed(3));
    this.fullscreenScaledElement = fullscreenEl;
  }

  private clearFocusModeFontScale(el?: HTMLElement): void {
    if (!el) return;
    el.style.removeProperty('--dvm-focus-scale');
    el.style.removeProperty('--dvm-font-scale');
  }

  private clampScale(value: number, min = 0.65, max = 3): number {
    return Math.min(max, Math.max(min, value));
  }

  /**
   * Get a representative font size from a chart option.
   * Used to detect when a chart's option has been replaced (fonts reset).
   */
  private getFontFingerprint(option: any): number {
    return option.xAxis?.[0]?.axisLabel?.fontSize
      || option.yAxis?.[0]?.axisLabel?.fontSize
      || option.legend?.[0]?.textStyle?.fontSize
      || option.series?.[0]?.label?.fontSize
      || 0;
  }

  /**
   * Re-scale ECharts fonts on all already-processed charts when
   * the effective font scale changes (e.g., window dragged between monitors).
   * Uses a correction factor (newScale / oldScale) to avoid compounding.
   */
  private rescaleAllChartFonts(): void {
    if (!this.autoResizeRoot) return;
    this.applyResponsiveGridToAll(this.autoResizeRoot);
  }

  /**
   * Scale all ECharts text font sizes by the given factor.
   */
  private scaleChartVisuals(chart: echarts.ECharts, option: any, factor: number): void {
    const ECHARTS_DEFAULT_FONT = 10;

    const scale = (size: any, fallback = ECHARTS_DEFAULT_FONT): number => {
      const px = typeof size === 'number' ? size : fallback;
      return Math.max(1, Math.round(px * factor));
    };

    const scaleTextStyle = (obj: any, fallback = ECHARTS_DEFAULT_FONT): any => {
      if (!obj) return { fontSize: scale(undefined, fallback) };
      const result: any = { fontSize: scale(obj.fontSize, fallback) };
      if (obj.rich) {
        result.rich = {};
        for (const key of Object.keys(obj.rich)) {
          result.rich[key] = { fontSize: scale(obj.rich[key]?.fontSize, fallback) };
        }
      }
      return result;
    };

    const scaleValue = (value: any): any => {
      if (typeof value === 'number') {
        const scaled = Math.round(value * factor * 100) / 100;
        return Object.is(scaled, -0) ? 0 : scaled;
      }
      if (Array.isArray(value)) {
        return value.map((item) => (
          typeof item === 'number'
            ? (() => {
                const scaled = Math.round(item * factor * 100) / 100;
                return Object.is(scaled, -0) ? 0 : scaled;
              })()
            : item
        ));
      }
      return value;
    };

    const scaleLineStyle = (obj: any, fallbackWidth?: number): any => {
      if (!obj && fallbackWidth == null) return undefined;
      const result: any = {};
      const width = obj?.width ?? fallbackWidth;
      if (width != null) result.width = scaleValue(width);
      if (obj?.shadowBlur != null) result.shadowBlur = scaleValue(obj.shadowBlur);
      if (obj?.shadowOffsetX != null) result.shadowOffsetX = scaleValue(obj.shadowOffsetX);
      if (obj?.shadowOffsetY != null) result.shadowOffsetY = scaleValue(obj.shadowOffsetY);
      return Object.keys(result).length ? result : undefined;
    };

    const scaleItemStyle = (obj: any): any => {
      if (!obj) return undefined;
      const result: any = {};
      if (obj.borderWidth != null) result.borderWidth = scaleValue(obj.borderWidth);
      if (obj.shadowBlur != null) result.shadowBlur = scaleValue(obj.shadowBlur);
      if (obj.shadowOffsetX != null) result.shadowOffsetX = scaleValue(obj.shadowOffsetX);
      if (obj.shadowOffsetY != null) result.shadowOffsetY = scaleValue(obj.shadowOffsetY);
      return Object.keys(result).length ? result : undefined;
    };

    const scaleAxisTick = (obj: any): any => {
      if (!obj) return undefined;
      const result: any = {};
      if (obj.length != null) result.length = scaleValue(obj.length);
      if (obj.lineStyle) {
        const lineStyle = scaleLineStyle(obj.lineStyle);
        if (lineStyle) result.lineStyle = lineStyle;
      }
      return Object.keys(result).length ? result : undefined;
    };

    const scaleLabelLine = (obj: any): any => {
      if (!obj) return undefined;
      const result: any = {};
      if (obj.length != null) result.length = scaleValue(obj.length);
      if (obj.length2 != null) result.length2 = scaleValue(obj.length2);
      if (obj.lineStyle) {
        const lineStyle = scaleLineStyle(obj.lineStyle);
        if (lineStyle) result.lineStyle = lineStyle;
      }
      return Object.keys(result).length ? result : undefined;
    };

    const patch: any = {};

    if (option.title?.length) {
      patch.title = option.title.map((t: any) => {
        const p: any = {};
        p.textStyle = scaleTextStyle(t.textStyle, 18);
        if (t.subtextStyle?.fontSize != null) p.subtextStyle = scaleTextStyle(t.subtextStyle);
        if (typeof t.top === 'number') p.top = scaleValue(t.top);
        return p;
      });
    }

    if (option.legend?.length) {
      patch.legend = option.legend.map((l: any) => ({
        textStyle: scaleTextStyle(l.textStyle),
        ...(l.itemWidth != null ? { itemWidth: scaleValue(l.itemWidth) } : {}),
        ...(l.itemHeight != null ? { itemHeight: scaleValue(l.itemHeight) } : {}),
        ...(l.itemGap != null ? { itemGap: scaleValue(l.itemGap) } : {}),
      }));
    }

    if (option.tooltip?.length) {
      patch.tooltip = option.tooltip.map((t: any) => ({
        textStyle: scaleTextStyle(t.textStyle, 12),
        ...(t.padding != null ? { padding: scaleValue(t.padding) } : {}),
      }));
    }

    if (option.xAxis?.length) {
      patch.xAxis = option.xAxis.map((axis: any) => {
        const p: any = { axisLabel: scaleTextStyle(axis.axisLabel) };
        if (axis.nameTextStyle?.fontSize != null) p.nameTextStyle = scaleTextStyle(axis.nameTextStyle);
        if (axis.nameGap != null) p.nameGap = scaleValue(axis.nameGap);
        if (axis.axisLine?.lineStyle) p.axisLine = { lineStyle: scaleLineStyle(axis.axisLine.lineStyle) };
        if (axis.axisTick) p.axisTick = scaleAxisTick(axis.axisTick);
        if (axis.axisPointer?.label) p.axisPointer = { label: scaleTextStyle(axis.axisPointer.label, 12) };
        if (axis.splitLine?.lineStyle) p.splitLine = { lineStyle: scaleLineStyle(axis.splitLine.lineStyle) };
        return p;
      });
    }

    if (option.yAxis?.length) {
      patch.yAxis = option.yAxis.map((axis: any) => {
        const p: any = { axisLabel: scaleTextStyle(axis.axisLabel) };
        if (axis.nameTextStyle?.fontSize != null) p.nameTextStyle = scaleTextStyle(axis.nameTextStyle);
        if (axis.nameGap != null) p.nameGap = scaleValue(axis.nameGap);
        if (axis.axisLine?.lineStyle) p.axisLine = { lineStyle: scaleLineStyle(axis.axisLine.lineStyle) };
        if (axis.axisTick) p.axisTick = scaleAxisTick(axis.axisTick);
        if (axis.axisPointer?.label) p.axisPointer = { label: scaleTextStyle(axis.axisPointer.label, 12) };
        if (axis.splitLine?.lineStyle) p.splitLine = { lineStyle: scaleLineStyle(axis.splitLine.lineStyle) };
        return p;
      });
    }

    if (option.series?.length) {
      patch.series = option.series.map((s: any) => {
        const seriesPatch: any = {};
        if (s.endLabel) seriesPatch.endLabel = scaleTextStyle(s.endLabel, 12);
        if (s.blur?.label) {
          seriesPatch.blur = { label: scaleTextStyle(s.blur.label, 12) };
        }
        if (s.select?.label) {
          seriesPatch.select = { label: scaleTextStyle(s.select.label, 12) };
        }
        if (s.lineStyle) {
          seriesPatch.lineStyle = scaleLineStyle(s.lineStyle, s.type === 'line' ? 2 : undefined);
        }
        if (s.itemStyle) {
          seriesPatch.itemStyle = scaleItemStyle(s.itemStyle);
        }
        if (s.emphasis?.itemStyle) {
          seriesPatch.emphasis = {
            ...(seriesPatch.emphasis || {}),
            itemStyle: scaleItemStyle(s.emphasis.itemStyle),
          };
        }
        if (s.symbolSize != null && typeof s.symbolSize !== 'function') {
          seriesPatch.symbolSize = scaleValue(s.symbolSize);
        }
        if (s.barWidth != null) seriesPatch.barWidth = scaleValue(s.barWidth);
        if (s.barMaxWidth != null) seriesPatch.barMaxWidth = scaleValue(s.barMaxWidth);
        if (s.barMinWidth != null) seriesPatch.barMinWidth = scaleValue(s.barMinWidth);
        if (s.labelLine) {
          seriesPatch.labelLine = scaleLabelLine(s.labelLine);
        }
        if (s.markPoint) {
          seriesPatch.markPoint = {
            ...(s.markPoint.symbolSize != null && typeof s.markPoint.symbolSize !== 'function'
              ? { symbolSize: scaleValue(s.markPoint.symbolSize) }
              : {}),
            ...(s.markPoint.label ? { label: scaleTextStyle(s.markPoint.label, 12) } : {}),
          };
        }
        if (s.markLine) {
          seriesPatch.markLine = {
            ...(s.markLine.label ? { label: scaleTextStyle(s.markLine.label, 12) } : {}),
            ...(s.markLine.lineStyle ? { lineStyle: scaleLineStyle(s.markLine.lineStyle) } : {}),
          };
        }
        if (s.markArea) {
          seriesPatch.markArea = {
            ...(s.markArea.label ? { label: scaleTextStyle(s.markArea.label, 12) } : {}),
          };
        }

        // Always scale series label (pie/donut/bar/line all have labels)
        // ECharts default label fontSize is 12
        seriesPatch.label = scaleTextStyle(s.label, 12);

        // Emphasis label (shown on hover — important for pie/donut)
        if (s.emphasis?.label) {
          seriesPatch.emphasis = {
            ...(seriesPatch.emphasis || {}),
            label: scaleTextStyle(s.emphasis.label, 12),
          };
        }

        // Treemap-specific
        if (s.upperLabel?.fontSize != null) seriesPatch.upperLabel = scaleTextStyle(s.upperLabel);
        if (s.levels?.length) {
          seriesPatch.levels = s.levels.map((level: any) => {
            const lp: any = {};
            if (level.label?.fontSize != null) lp.label = scaleTextStyle(level.label);
            if (level.upperLabel?.fontSize != null) lp.upperLabel = scaleTextStyle(level.upperLabel);
            return lp;
          });
        }
        return seriesPatch;
      });
    }

    chart.setOption(patch);
  }

  stopChartAutoResize(): void {
    if (this.gridScanTimer) {
      clearInterval(this.gridScanTimer);
      this.gridScanTimer = undefined;
    }
    this.chartResizeObserver?.disconnect();
    this.chartMutationObserver?.disconnect();
    this.chartResizeObserver = undefined;
    this.chartMutationObserver = undefined;
    this.autoResizeRoot = undefined;
  }

  destroy(): void {
    this.stopChartAutoResize();
    this.resizeSub?.unsubscribe();
    this.fontScaleMap.clear();
    this.fontFingerprints.clear();
    this.lastEffectiveScale = 1;
    this.compactLayoutFitOverride = null;
    document.documentElement.classList.remove('dvm-no-scroll');
    this.clearFocusModeFontScale(this.fullscreenScaledElement);
    this.fullscreenScaledElement = undefined;
    this._screenFloating = true;
    this.initialized = false;
  }
}
